import { constants, JobType } from "@common/_constants";
import { refetch } from "@common/_services";
import { Button } from "@common/components/Atoms/Button";
import { HelpButton } from "@common/components/Atoms/HelpButton";
import {
    IconEmail,
    IconFacebook,
    IconLinkedin,
    IconTwitter,
} from "@common/components/Atoms/Icons";
import { TelLink } from "@common/components/Atoms/Links";
import RelodeRep from "@common/components/Atoms/RelodeRep";
import * as React from "react";
import { Link } from "react-router-dom";
import { Sticky, StickyContainer } from "react-sticky";

interface JobShareIconsProps {
    job: {
        job_id: number;
    };
    showButtons: boolean;
}

interface JobShareIconsState {
    updating: boolean;
    loading: boolean;
    error: boolean;
    errorMessage: string;
    emailLink: string;
    facebookLink: string;
    linkedInLink: string;
    twitterLink: string;
}

class JobShareIcons extends React.Component<
    JobShareIconsProps,
    JobShareIconsState
> {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            updating: false,
            error: false,
            errorMessage: "",
            emailLink: "",
            facebookLink: "",
            linkedInLink: "",
            twitterLink: "",
        };

        this.onAfterOpen = this.onAfterOpen.bind(this);
    }

    componentDidMount() {
        this.onAfterOpen();
    }

    onAfterOpen() {
        const url = `${constants.GlobalConfig.apiUrl}/pro/v2/jobs/${this.props.job.job_id}/sharelinks`;

        const settings = {
            headers: {
                "Content-Type": "application/json",
            },
            method: "GET",
        };

        refetch(url, settings)
            .then(resp => resp.json())
            .then(json => {
                this.setState({
                    emailLink: json.result.email_url,
                    facebookLink: json.result.facebook_url,
                    linkedInLink: json.result.linked_in_url,
                    twitterLink: json.result.twitter_url,
                });
            })
            .catch(err => {
                console.log(err);
                if (err.type === "abort" || err.name === "AbortError") return;
                this.setState({
                    error: true,
                    errorMessage: err.message,
                    updating: false,
                    loading: false,
                });
            });
    }

    render() {
        const {
            emailLink,
            facebookLink,
            linkedInLink,
            twitterLink,
        } = this.state;

        const buttons = this.props.showButtons ? (
            <div>
                <a className="c-button c-button__margin-right" href={emailLink}>
                    <IconEmail />
                </a>
                <a
                    className="c-button c-button--facebook c-button--no-text c-button__margin-right"
                    href={facebookLink}
                    target="_blank"
                >
                    <IconFacebook />
                </a>
                <a
                    className="c-button c-button--linkedin c-button--no-text c-button__margin-right"
                    href={linkedInLink}
                    target="_blank"
                >
                    <IconLinkedin />
                </a>
                <a
                    className="c-button c-button--twitter c-button--no-text"
                    href={twitterLink}
                    target="_blank"
                >
                    <IconTwitter />
                </a>
            </div>
        ) : (
            <div>
                <ul className="c-icon_social--list">
                    <li>
                        <a href={emailLink}>
                            <IconEmail />{" "}
                        </a>
                    </li>
                    <li>
                        <a href={facebookLink} target="_blank">
                            <IconFacebook />{" "}
                        </a>
                    </li>
                    <li>
                        <a href={linkedInLink} target="_blank">
                            <IconLinkedin />{" "}
                        </a>
                    </li>
                    <li>
                        <a href={twitterLink} target="_blank">
                            <IconTwitter />{" "}
                        </a>
                    </li>
                </ul>
            </div>
        );

        return <React.Fragment>{buttons}</React.Fragment>;
    }
}

interface JobDetailActionsProps {
    job: {
        job_id: number;
        user_has_access: boolean;
        is_open: boolean;
    };
    signAgreement: () => void;
}

// small screen page-level actions
export const JobDetailActions: React.SFC<JobDetailActionsProps> = (
    props: JobDetailActionsProps
) => {
    if (!props.job.user_has_access) {
        return (
            <ul className="c-detail__actions">
                <li>
                    <JobShareIcons showButtons={true} job={props.job} />
                </li>
                <li>
                    <a
                        className="c-link c-link--magenta"
                        onClick={props.signAgreement}
                    >
                        add to my jobs
                    </a>
                </li>
            </ul>
        );
    } else if (props.job.is_open) {
        return (
            <ul className="c-detail__actions">
                <li>
                    <JobShareIcons showButtons={true} job={props.job} />
                </li>
            </ul>
        );
    }
    return null;
};

interface JobDetailCompanyNameProps {
    shouldShowCompany: boolean;
    openModal: (string) => void;
    job: {
        company_name: string;
    };
}

// link to open company name modal
export const JobDetailCompanyName: React.SFC<JobDetailCompanyNameProps> = (
    props: JobDetailCompanyNameProps
) => {
    if (!props.shouldShowCompany) {
        return (
            <a
                className="c-link c-link--magenta"
                onClick={() =>
                    props.openModal("showCompanyAgreementModalIsOpen")
                }
            >
                show company/team
            </a>
        );
    }
    return <span>{props.job.company_name}</span>;
};

// small screen sticky footer
// TODO We need to investigate if this is really used.
// Initial investigation suggests that it is not, or at least it never appears on screen.
interface JobDetailModel {
    job_id: number;
    is_open: boolean;
    total_commission_in_us_dollars: string;
    job_type_id: JobType;
    open_positions: number;
    employment_guarantee: string;
    employment_guarantee_payment: string;
    user_has_access: boolean;
}

interface JobDetailFooterProps {
    job: JobDetailModel;
    openModal: (string) => void;
}

const ReferralAmount = (props: {
    job: { total_commission_in_us_dollars: string; job_type_id: JobType };
    openModal: (name: string) => void;
}) => (
    <>
        {!!props.job.total_commission_in_us_dollars ? (
            <>
                <div
                    style={{ display: "block" }}
                    className="c-detail__openings"
                >
                    rewards up to
                </div>
                <a
                    className="c-link c-link--magenta c-link--light-text"
                    onClick={() => props.openModal("rewardDetailsModalIsOpen")}
                >
                    {props.job.total_commission_in_us_dollars}
                </a>{" "}
            </>
        ) : (
            <>referral reward not available</>
        )}
    </>
);
export const JobDetailFooter: React.SFC<JobDetailFooterProps> = (
    props: JobDetailFooterProps
) => {
    if (props.job.is_open) {
        return (
            <section className="c-detail__footer">
                <div className="c-detail__footer-message">
                    <ReferralAmount
                        job={props.job}
                        openModal={props.openModal}
                    />
                </div>
                <div style={{ whiteSpace: "nowrap" }}>
                    <Button
                        id="refer-modal-button"
                        large={true}
                        green={true}
                        style={{ marginLeft: "8px" }}
                        onClick={() =>
                            props.openModal("referCandidateModalIsOpen")
                        }
                    >
                        refer
                    </Button>
                    <Button
                        id="apply-modal-button"
                        large={true}
                        onClick={() =>
                            props.openModal("applyForJobModalIsOpen")
                        }
                    >
                        apply
                    </Button>
                </div>
            </section>
        );
    }
    return null;
};

interface JobDetailSummaryProps {
    job: JobDetailModel;
    openModal: (string) => void;
    signAgreement: () => void;
    openSendMessage: () => void;
    userManager: {
        display_name: string;
        profile_image_url: string;
        phone_number: string;
    };
}

// large screen summary box
export const JobDetailSummary: React.SFC<JobDetailSummaryProps> = (
    props: JobDetailSummaryProps
) => (
    <StickyContainer className="c-detail__sidebar-large-stickycontainer">
        <Sticky topOffset={-50} disableCompensation={true}>
            {({ style, isSticky }) => {
                return (
                    <section
                        className="c-detail-summary"
                        style={{
                            ...style,
                            marginTop: isSticky ? "100px" : "0",
                        }}
                    >
                        {props.job.is_open ? (
                            <div className="c-detail-summary__large-header">
                                <h3 className="c-detail-summary__title">
                                    <ReferralAmount
                                        job={props.job}
                                        openModal={props.openModal}
                                    />
                                    <span className="c-detail__openings">
                                        {props.job.open_positions} opening
                                        {props.job.open_positions === 1
                                            ? ""
                                            : "s"}
                                    </span>
                                </h3>
                            </div>
                        ) : (
                            <div className="c-detail-summary__header">
                                <h3 className="c-detail-summary__title">
                                    job closed/paused
                                </h3>
                            </div>
                        )}
                        <div className="c-detail-summary__body">
                            {props.job.is_open ? (
                                <>
                                    <div className="c-detail-summary__buttons">
                                        <Button
                                            id="refer-a-friend-button"
                                            green={true}
                                            block={true}
                                            onClick={() =>
                                                props.openModal(
                                                    "referCandidateModalIsOpen"
                                                )
                                            }
                                        >
                                            refer a candidate
                                        </Button>
                                        <Button
                                            id="apply-for-position-button"
                                            block={true}
                                            onClick={() =>
                                                props.openModal(
                                                    "applyForJobModalIsOpen"
                                                )
                                            }
                                        >
                                            apply for position
                                        </Button>
                                    </div>
                                    <div className="c-detail-summary__message">
                                        the reward is paid either way!
                                    </div>
                                </>
                            ) : (
                                <div className="c-detail-summary__message c-detail-summary__message--large">
                                    <p>
                                        <strong>
                                            This job is not accepting referrals
                                            at this time.
                                        </strong>
                                    </p>
                                    <p>
                                        Please refer candidates to{" "}
                                        <Link
                                            to="/jobs"
                                            className="c-link c-link--magenta"
                                        >
                                            another position
                                        </Link>
                                        .
                                    </p>
                                </div>
                            )}
                            {props.job.user_has_access &&
                                props.userManager.display_name &&
                                props.userManager.profile_image_url && (
                                    <div className="c-detail-summary__manager">
                                        <h4 className="c-detail-summary__manager-title">
                                            questions?
                                        </h4>
                                        <RelodeRep
                                            image={
                                                props.userManager
                                                    .profile_image_url
                                            }
                                            name={
                                                props.userManager.display_name
                                            }
                                        >
                                            {props.userManager.phone_number && (
                                                <TelLink
                                                    display={
                                                        props.userManager
                                                            .phone_number
                                                    }
                                                    tel={
                                                        props.userManager
                                                            .phone_number
                                                    }
                                                />
                                            )}
                                        </RelodeRep>
                                    </div>
                                )}
                        </div>
                        {props.job.is_open && (
                            <div className="c-detail-summary__footer">
                                <JobShareIcons
                                    showButtons={false}
                                    job={props.job}
                                />
                                {!props.job.user_has_access && (
                                    <a
                                        className="c-link c-link--magenta no-underline"
                                        onClick={props.signAgreement}
                                    >
                                        add to my jobs
                                    </a>
                                )}

                                {props.job.user_has_access && (
                                    <a
                                        className="c-link c-link--magenta no-underline"
                                        onClick={props.openSendMessage}
                                    >
                                        send message
                                    </a>
                                )}
                            </div>
                        )}
                    </section>
                );
            }}
        </Sticky>
    </StickyContainer>
);
